import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Plus, MapPinned } from "lucide-react";
import { toast } from "sonner";
import { todayISO, fmtDate, fmtWeekdayShort, fmtMonthShort, dayOfMonthIST, istDateKey } from "@/lib/utils-app";

const WORK_TYPES = ["road", "OSR", "drain", "electrical", "plumbing", "landscape", "other"];

// Park-asset types map onto work_type when applicable. When the admin
// selects one of these work types we offer a master-data picker.
const WORK_TYPE_TO_ASSET_TYPE = {
  road: "road",
  OSR: "osr",
  drain: null,
  electrical: null,
  plumbing: null,
  landscape: null,
  other: null,
};

function startOfWeek(d) {
  const dt = new Date(d);
  const day = dt.getDay(); // 0=Sun
  const diff = dt.getDate() - day + (day === 0 ? -6 : 1); // Monday-start
  const m = new Date(dt.setDate(diff));
  m.setHours(0, 0, 0, 0);
  return m;
}
function addDays(d, n) {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
}
function iso(d) { return istDateKey(d); }

export default function PlannerPage() {
  const [parks, setParks] = useState([]);
  const [parkId, setParkId] = useState("");
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [plans, setPlans] = useState([]);
  const [assets, setAssets] = useState([]); // park-assets master for selected park
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ plan_date: todayISO(), work_type: "road", location: "", description: "", asset_id: "" });
  const [editing, setEditing] = useState(null);

  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideTarget, setOverrideTarget] = useState(null);
  const [overrideForm, setOverrideForm] = useState({ new_location: "", reason: "" });

  useEffect(() => {
    api.get("/parks").then((r) => {
      setParks(r.data);
      if (r.data[0]) setParkId(r.data[0].id);
    });
  }, []);

  const load = useCallback(async () => {
    if (!parkId) return;
    setLoading(true);
    try {
      const start = iso(weekStart);
      const end = iso(addDays(weekStart, 6));
      const { data } = await api.get("/work-plans", { params: { park_id: parkId, start, end } });
      setPlans(data);
    } catch { toast.error("Failed to load plans"); }
    finally { setLoading(false); }
  }, [parkId, weekStart]);
  useEffect(() => { load(); }, [load]);

  // Load active park assets whenever the selected park changes so the
  // plan-item dialog can offer a cascading asset picker.
  useEffect(() => {
    if (!parkId) { setAssets([]); return; }
    let alive = true;
    api.get("/admin/park-assets", { params: { park_id: parkId, active_only: true } })
      .then((r) => { if (alive) setAssets(r.data || []); })
      .catch(() => { if (alive) setAssets([]); });
    return () => { alive = false; };
  }, [parkId]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const byDate = useMemo(() => {
    const m = {}; for (const p of plans) (m[p.plan_date] = m[p.plan_date] || []).push(p); return m;
  }, [plans]);

  const openCreate = (date) => { setEditing(null); setForm({ plan_date: iso(date), work_type: "road", location: "", description: "", asset_id: "" }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ plan_date: p.plan_date, work_type: p.work_type, location: p.location, description: p.description || "", asset_id: p.asset_id || "" }); setOpen(true); };

  const matchedAssetType = WORK_TYPE_TO_ASSET_TYPE[form.work_type] || null;
  const assetOptions = matchedAssetType ? assets.filter((a) => a.asset_type === matchedAssetType) : [];

  const submit = async () => {
    // Either a free-text location OR a picked asset is acceptable; we
    // require at least one of the two.
    const pickedAsset = form.asset_id ? assets.find((a) => a.id === form.asset_id) : null;
    const effectiveLocation = form.location?.trim() || pickedAsset?.name || "";
    if (!effectiveLocation) return toast.error("Pick an asset or enter a location");
    const payload = {
      ...form,
      location: effectiveLocation,
      park_id: parkId,
      asset_id: form.asset_id || null,
      asset_type: pickedAsset?.asset_type || (matchedAssetType || null),
    };
    try {
      if (editing) await api.put(`/work-plans/${editing.id}`, payload);
      else await api.post("/work-plans", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm("Delete this plan item?")) return;
    try { await api.delete(`/work-plans/${p.id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  const startOverride = (p) => { setOverrideTarget(p); setOverrideForm({ new_location: p.overridden_location || "", reason: "" }); setOverrideOpen(true); };
  const submitOverride = async () => {
    if (!overrideForm.new_location) return toast.error("New location required");
    try { await api.post(`/work-plans/${overrideTarget.id}/override`, overrideForm); toast.success("Override saved"); setOverrideOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <AppShell title="Work Planner" subtitle="Weekly day-to-day works for each park (admin can override locations on the fly)" action={
      <div className="flex flex-wrap items-center gap-2">
        <Select value={parkId} onValueChange={setParkId}>
          <SelectTrigger className="w-[220px]" data-testid="planner-park-select"><SelectValue /></SelectTrigger>
          <SelectContent>{parks.map((p)=><SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="outline" onClick={()=>setWeekStart(addDays(weekStart, -7))} data-testid="planner-prev-week"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="px-2 text-xs text-muted-foreground">{iso(weekStart)} → {iso(addDays(weekStart, 6))}</div>
          <Button size="icon" variant="outline" onClick={()=>setWeekStart(addDays(weekStart, 7))} data-testid="planner-next-week"><ChevronRight className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" onClick={()=>setWeekStart(startOfWeek(new Date()))}>Today</Button>
        </div>
      </div>
    }>
      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
        : <div className="grid gap-3 md:grid-cols-7">
            {days.map((d) => {
              const items = byDate[iso(d)] || [];
              const isToday = iso(d) === todayISO();
              return (
                <Card key={iso(d)} className={`min-h-[180px] ${isToday ? "ring-1 ring-primary/40" : ""}`} data-testid={`planner-day-${iso(d)}`}>
                  <CardHeader className="py-3">
                    <CardTitle className="flex items-center justify-between text-sm">
                      <span>
                        <span className="text-xs text-muted-foreground uppercase tracking-wide">{fmtWeekdayShort(d)}</span>
                        <div className="font-display">{dayOfMonthIST(d)} {fmtMonthShort(d)}</div>
                      </span>
                      <Button size="icon" variant="ghost" onClick={()=>openCreate(d)} data-testid={`planner-add-${iso(d)}`}><Plus className="h-4 w-4" /></Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-2">
                    {items.length === 0 ? <div className="rounded-lg border border-dashed p-3 text-center text-xs text-muted-foreground">No plans</div>
                      : items.map((p) => (
                        <div key={p.id} className={`rounded-lg border p-2 text-xs ${p.overridden_location ? "bg-amber-50 ring-1 ring-amber-300" : ""}`} data-testid={`planner-item-${p.id}`}>
                          <div className="flex items-center justify-between gap-1">
                            <span className="rounded bg-secondary px-1.5 py-0.5 font-mono text-[10px] uppercase">{p.work_type}</span>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={()=>openEdit(p)}><Plus className="h-3 w-3 rotate-45" /></Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={()=>startOverride(p)} data-testid="planner-override-button"><MapPinned className="h-3 w-3" /></Button>
                            </div>
                          </div>
                          <div className="mt-1 font-medium">{p.location}</div>
                          {p.overridden_location && <div className="mt-0.5 text-[11px] text-amber-700">→ overridden to: <span className="font-medium">{p.overridden_location}</span></div>}
                          {p.description && <div className="mt-0.5 text-muted-foreground line-clamp-2">{p.description}</div>}
                          <button onClick={()=>remove(p)} className="mt-1 text-[10px] text-red-600">Delete</button>
                        </div>
                      ))
                    }
                  </CardContent>
                </Card>
              );
            })}
          </div>
      }

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit work item" : "Add work item"}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <FieldLite label="Date"><Input type="date" value={form.plan_date} onChange={(e)=>setForm({...form,plan_date:e.target.value})} data-testid="plan-date-input" /></FieldLite>
              <FieldLite label="Work type">
                <Select value={form.work_type} onValueChange={(v)=>setForm({...form,work_type:v, asset_id: ""})}>
                  <SelectTrigger data-testid="plan-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{WORK_TYPES.map((t)=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </FieldLite>
            </div>
            {matchedAssetType ? (
              <FieldLite label={`Asset (${matchedAssetType.toUpperCase()})`}>
                {assetOptions.length === 0 ? (
                  <div className="rounded-md border border-dashed bg-amber-50/40 p-2 text-xs text-amber-800">
                    No <span className="font-medium">{matchedAssetType}</span> assets exist for this park yet. Add them under
                    <span className="font-medium"> Settings → Data Management → Park Assets</span>, or just use the free-text location below.
                  </div>
                ) : (
                  <Select value={form.asset_id || "none"} onValueChange={(v) => {
                    if (v === "none") {
                      setForm({ ...form, asset_id: "" });
                    } else {
                      const a = assets.find((x) => x.id === v);
                      setForm({ ...form, asset_id: v, location: a?.name || form.location });
                    }
                  }}>
                    <SelectTrigger data-testid="plan-asset-select"><SelectValue placeholder="Pick an asset…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None (use free-text location) —</SelectItem>
                      {assetOptions.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name}{a.length_km != null ? ` (${a.length_km} ${a.asset_type === "osr" ? "acres" : "km"})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </FieldLite>
            ) : null}
            <FieldLite label="Location *"><Input value={form.location} onChange={(e)=>setForm({...form,location:e.target.value})} data-testid="plan-location-input" placeholder={form.asset_id ? "(filled from asset)" : "Free-text location"} /></FieldLite>
            <FieldLite label="Description"><Textarea value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} /></FieldLite>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="plan-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Override location</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="text-xs text-muted-foreground">Original: <span className="font-medium">{overrideTarget?.location}</span></div>
            <FieldLite label="New location *"><Input value={overrideForm.new_location} onChange={(e)=>setOverrideForm({...overrideForm,new_location:e.target.value})} data-testid="plan-override-location-input" /></FieldLite>
            <FieldLite label="Reason"><Textarea value={overrideForm.reason} onChange={(e)=>setOverrideForm({...overrideForm,reason:e.target.value})} /></FieldLite>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOverrideOpen(false)}>Cancel</Button>
            <Button onClick={submitOverride} data-testid="plan-override-save">Override</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function FieldLite({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
